import { globalStateCTX } from '../../../GlobalState/GlobalState';
import { useNonce } from '../../../Utils/hooks';
import NumberFormater from '../../NumberFormater/NumberFormater';
import KpiCard, { KPIStatus } from '../../KPIs/KPICard/KpiCard';
import React from 'react';
import { formatCurrency, getComparedPercentage } from '../../../Utils/number';
import { faArrowDown, faArrowUp, faMinus } from '@fortawesome/pro-solid-svg-icons';
import colors from '../../../Utils/Colors/Colors.module.scss';
import { statisticsApi } from '../../../Http/Http';
import { TotalRentReadView } from '../../../GeneratedServices';
import { useTranslation } from 'react-i18next';
import { FilterValuesType } from '../../../FiltersProvider/FiltersProvider';

interface Props {
  filterValues?: FilterValuesType;
  propertyId?: number;
  reloadNonce?: number;
}
const TotalRentKpi: React.FC<Props> = (props) => {
  const { t } = useTranslation(['contracts', 'common']);

  const { handleHttpErrors } = React.useContext(globalStateCTX);

  const [totalRentStatistics, setTotalRentStatistics] = React.useState<TotalRentReadView>();
  const [status, setStatus] = React.useState<KPIStatus>(KPIStatus.Loading);
  const [shouldReload, reload] = useNonce();

  React.useEffect(() => {
    const abortController = new AbortController();

    const load = async () => {
      try {
        setStatus(KPIStatus.Loading);
        const { data } = await statisticsApi.statisticsResourceGetTotalRent(
          {
            ...props.filterValues,
            propertyId: props.propertyId
          },
          {
            signal: abortController.signal
          }
        );

        setTotalRentStatistics(data);
        setStatus(KPIStatus.None);
      } catch (error) {
        handleHttpErrors(error) && setStatus(KPIStatus.LoadError);
      }
    };
    load();

    return () => abortController.abort();
  }, [props.filterValues, props.propertyId, handleHttpErrors, shouldReload, props.reloadNonce]);

  const comparedTotalRent =
    totalRentStatistics?.plannedValue &&
    getComparedPercentage(totalRentStatistics.actualValue, totalRentStatistics.plannedValue);

  const hasBussinesPlanValue = totalRentStatistics?.plannedValue;
  return (
    <KpiCard
      title={t('contracts:totalRent')}
      value={
        <NumberFormater
          value={formatCurrency(totalRentStatistics?.actualValue ?? 0, { forceCurrency: 'KSEK' }).value}
          additionalProps={{ decimalScale: 0 }}
        />
      }
      valueSuffix={'KSEK'}
      extra={
        hasBussinesPlanValue ? (
          <NumberFormater
            value={comparedTotalRent}
            suffix={t('common:vsBusinessPlan')}
            additionalProps={{ decimalScale: 1 }}
          />
        ) : (
          t('common:noBusinessPlanInformation')
        )
      }
      extraColor={
        hasBussinesPlanValue ? (comparedTotalRent && comparedTotalRent > 0 ? colors.green : colors.red) : colors.grey
      }
      extraIcon={
        hasBussinesPlanValue ? (comparedTotalRent && comparedTotalRent > 0 ? faArrowUp : faArrowDown) : faMinus
      }
      additionalValue={
        totalRentStatistics?.actualValuePerArea != null ? (
          <NumberFormater
            value={formatCurrency(totalRentStatistics?.actualValuePerArea ?? 0, { forceCurrency: 'SEK' }).value}
            additionalProps={{ decimalScale: 0 }}
          />
        ) : (
          t('common:none')
        )
      }
      additionalValueSuffix={t('common:sekSqm')}
      includeExtra
      retry={reload}
      status={status}
    />
  );
};

export default TotalRentKpi;
